// src/services/checkAnalysisService.js
//
// Rellenar el formulario "Nuevo cheque emitido" con una IA externa (la cuenta
// de Gemini de quien registra): se copia el prompt, se pega en la IA junto con
// la foto o el PDF del cheque, y la respuesta (un JSON con los campos exactos)
// se pega de vuelta. No hay llamadas a ningún servidor: nada sale de la app.
import { PAYMENT_TYPES } from '../documents/closingData';

const asText = value => (typeof value === 'string' ? value.trim() : '');
const asAmount = value => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
};
// Solo YYYY-MM-DD válido; cualquier otro formato se descarta (el campo queda como estaba).
const asDate = value => {
  const text = asText(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return '';
  const date = new Date(`${text}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== text ? '' : text;
};
const asPaymentType = value => (PAYMENT_TYPES.some(type => type.value === value) ? value : '');

// Lo que devuelve la IA (o una persona, a mano) → valores seguros para el formulario.
export const normalizeCheckRead = raw => {
  const data = raw && typeof raw === 'object' ? raw : {};
  const mortgageCompany = asText(data.mortgageCompany);
  return {
    amount: asAmount(data.amount),
    checkNumber: asText(data.checkNumber),
    checkDate: asDate(data.checkDate),
    payableTo: asText(data.payableTo),
    paymentType: asPaymentType(asText(data.paymentType)),
    mortgageCompany,
    hasMortgage: Boolean(mortgageCompany),
    insuranceCompanyName: asText(data.insuranceCompanyName),
    claimNumber: asText(data.claimNumber),
    warnings: (Array.isArray(data.warnings) ? data.warnings : []).map(asText).filter(Boolean)
  };
};

// Aplica la lectura al formulario sin borrar lo que ya estaba escrito: solo
// pisa los campos que la IA sí leyó. El % del PA nunca se toca.
export const applyCheckRead = (form, read) => ({
  ...form,
  ...(read.amount !== null ? { amount: String(read.amount) } : {}),
  ...(read.checkNumber ? { checkNumber: read.checkNumber } : {}),
  ...(read.checkDate ? { checkDate: read.checkDate } : {}),
  ...(read.payableTo ? { payableTo: read.payableTo } : {}),
  ...(read.paymentType ? { paymentType: read.paymentType } : {}),
  ...(read.mortgageCompany ? { hasMortgage: true } : {})
});

// Prompt para la IA externa: instrucciones + las claves EXACTAS del JSON de respuesta.
const ANSWER_SHAPE = {
  amount: 0,
  checkNumber: '',
  checkDate: 'YYYY-MM-DD',
  payableTo: '',
  mortgageCompany: '',
  insuranceCompanyName: '',
  claimNumber: '',
  paymentType: '',
  warnings: ['Dato que no se leyó o es ambiguo']
};

export const buildCheckPrompt = () => [
  'Eres asistente de un Public Adjuster en Florida. Lee el cheque (o aviso de pago) de una aseguradora que aparece en la imagen o PDF adjunto y extrae sus datos.',
  '',
  'Reglas:',
  '- Usa SOLO lo que está escrito en el documento. No inventes ni supongas: si un dato no se ve o es ilegible, deja el texto vacío, el número en null y anótalo en "warnings".',
  '- "amount": monto en dólares como número, sin símbolos ni comas (ej. 2059.39). Si el monto en números y en letras no coinciden, usa el que se lee con más claridad y avisa en "warnings".',
  '- "checkDate": fecha de emisión en formato YYYY-MM-DD.',
  '- "checkNumber": número del cheque (arriba a la derecha y en la línea MICR de abajo).',
  '- "payableTo": beneficiario completo tal como aparece (todos los nombres y empresas, incluido el banco/mortgage si figura).',
  '- "mortgageCompany": nombre del banco o mortgage si aparece como co-beneficiario; si no, vacío.',
  '- "insuranceCompanyName": aseguradora que emite el cheque.',
  '- "claimNumber": número de reclamo si aparece en el cheque o en el talón.',
  `- "paymentType": elige UNO solo si el talón o memo lo dice claramente (${PAYMENT_TYPES.map(type => type.value).join(', ')}; la última es depreciación recuperable); si no, vacío.`,
  '',
  'Formato de la respuesta: devuelve SOLO un objeto JSON válido, sin texto antes ni después y sin comentarios, con exactamente estas claves:',
  JSON.stringify(ANSWER_SHAPE, null, 2)
].join('\n');

// Lee la respuesta pegada: tolera texto alrededor y bloques ```json.
export const parseCheckAnswer = text => {
  const raw = String(text || '');
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) throw new Error('No encontré el JSON en la respuesta. Copia la respuesta completa de la IA.');
  let parsed;
  try {
    parsed = JSON.parse(raw.slice(start, end + 1));
  } catch (error) {
    throw new Error('La respuesta no es un JSON válido (quizás se cortó). Pídele a la IA que la repita completa y en un solo bloque.');
  }
  if (Object.keys(ANSWER_SHAPE).filter(key => key in parsed).length < 3) {
    throw new Error('La respuesta no tiene el formato del cheque. Revisa que la IA haya usado las instrucciones copiadas.');
  }
  return normalizeCheckRead(parsed);
};
